import app from "../app";
import throttle from 'lodash/throttle'
import debounce from 'lodash/debounce'
import * as viewport from "../utilities/viewport";

export class Video {
  static _uid = 0;

  constructor(placeholderEl = null, callbackOrOptions = null) {
    // Per-instance event namespace
    this._ns = `.lazyLoadVideo.${++Video._uid}`;

    // Optional callback: function or { onLoadedmetadata: fn }
    this._onLoadedmetadata =
      typeof callbackOrOptions === 'function'
        ? callbackOrOptions
        : (callbackOrOptions && typeof callbackOrOptions.onLoadedmetadata === 'function'
            ? callbackOrOptions.onLoadedmetadata
            : null);

    // If a placeholder element is passed, only operate on that one
    this.$placeholders = placeholderEl
      ? $(placeholderEl).filter('.lazy-video-placeholder')
      : $('.lazy-video-placeholder');

    if (!this.$placeholders.length) return;

    this.initialize();
    this.removePersistedVideos();

    document.addEventListener('turbo:before-cache', () => {
      this.turnOffEventListeners();
    }, { once: true });
  }

  removePersistedVideos() {
    document.querySelectorAll('.turbo-persist-video').forEach((video) => {
      video.pause();
      video.src = '';
      video.remove();
      video = null;
    });
  }

  turnOffEventListeners() {
    // Clean up only this instance's videos/listeners
    if (this.$videos && this.$videos.length) {
      this.$videos.each((index, video) => {
        if (video.classList.contains('turbo-persist-video')) return;
        video.pause();
        video.src = '';
        video.remove();
        video = null;
      });
    }
    this.$videos = null;
    this.$placeholders && this.$placeholders.removeClass('lazy-video-initialized');

    // Remove only this instance's window listeners
    app.$window.off('resize' + this._ns);
    app.$window.off('scroll' + this._ns);

    // Do NOT remove the global document click handler here; it is set outside the class.
    // app.$document.off('click.lazyLoadVideo', '.lazy-video'); // (intentionally removed)
  }

  initialize() {
    if (!this.$placeholders.length) return;

    const self = this;
    this.$placeholders.each(function() {
      var $placeholder = $(this);

      if ($placeholder.hasClass('lazy-video-initialized')) return;
      if ($placeholder.hasClass('lazy-video-delay-initialization')) return;

      var videoHTML = $placeholder.attr('data-video-tag-html');
      var $video = $(videoHTML);

      if ($placeholder.hasClass('desktop-video') && app.breakpointIsMobile()) return;
      if ($placeholder.hasClass('mobile-video') && !app.breakpointIsMobile()) return;

      var srcFull = $video.attr('data-src');
      var srcMobile = $video.attr('data-src-mobile');
      var src = (app.breakpointIsMobile() && srcMobile) ? srcMobile : srcFull;

      $video.addClass('lazy-video').attr('src', src);
      $placeholder.addClass('lazy-video-initialized').hide().after($video);

      $video.one('loadedmetadata', function() {
        $video.closest('.video-jumpfix').addClass('has-loadedmetadata');
        // User-provided callback, if any
        if (typeof self._onLoadedmetadata === 'function') {
          try {
            self._onLoadedmetadata($video[0], $video, $placeholder[0]);
          } catch (e) {
            console.warn('onLoadedmetadata callback threw an error:', e);
          }
        }
      });
    });

    // Scope videos to the ones created next to our placeholders (not global)
    this.$videos = this.$placeholders.next('video.lazy-video');

    var checkForVisibility = () => {
      if (!this.$videos || !this.$videos.length) return;

      this.$videos.each((index, element) => {
        var $video = $(element);

        // Don't try to check visibility for videos that explicitly disable the functionality
        if ($video.attr('data-ignore-video-visibility-listeners')) return;

        var video = $video[0];
        var autoplay = $video.attr('autoplay');
        var isInHero = $video.closest('#hero-viewport-container').length;
        var isVisible = false;

        if (!autoplay || autoplay == 'false') {
          $video.data('lazy-load-video-autoplay-disabled', true);
          return;
        }

        if (isInHero) {
          if (app.scrollTop < app.windowHeight / 2) {
            isVisible = true;
          }
        } else if (viewport.isVisible(video)) {
          if ($video.data('has-been-paused-by-user')) return;
          if ($video.data('hasFinishedPlaying') == true && ( !loop || loop == 'false' )) return; // existing logic retained
          isVisible = true;
        }

        if (isVisible && !$video.is(':visible')) {
          isVisible = false;
        }

        if (isVisible) {
          var playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.then(function() {
              // Automatic playback started
            }).catch(function(error) {
              // Auto-play was prevented
              console.warn('Error playing video', error);
            });
          }
        } else {
          video.pause();
        }
      });
    };

    // Per-instance, namespaced listeners
    app.$window.on('resize' + this._ns, debounce(checkForVisibility, 500));
    app.$window.on('scroll' + this._ns, throttle(checkForVisibility, 500));
    checkForVisibility();

    var $disabledVideos = this.$placeholders.filter(function() {
      return $(this).data('lazy-load-video-autoplay-disabled') == true;
    });

    if (this.$placeholders.length == $disabledVideos.length) {
      this.turnOffEventListeners();
    }
  }
}

// Export an init function that looks for and instantiates the module on pageload
export default (() => {
  app.pageLoad.push(() => {
    // Backwards compatibility: still auto-initialize all placeholders on pageload
    $('.lazy-video-placeholder').each(function() {
      new Video(this);
    });

    // Global click handler kept as-is
    app.$document.on('click.lazyLoadVideo', '.lazy-video', function() {
      var $video = $(this);

      // Don't pause videos in the footer on click
      if ($video.closest('#footer__next-project').length) return;

      // Don't pause videos that have a ignore-video-visibility-listeners attribute
      if ($video.closest('[data-ignore-video-visibility-listeners]').length) return;

      if ($video[0].paused) {
        $video.data('has-been-paused-by-user', false);
        $video[0].play();
      } else {
        $video.data('has-been-paused-by-user', true);
        $video[0].pause();
      }
    });
  });
})();
